import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { brand, services } from "../mock";
import { trackEvent } from "../components/Analytics";
import { submitLead } from "../lib/api";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    suburb: "",
    bedrooms: "",
    bathrooms: "",
    service: "",
    frequency: "one-off",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Please complete the required fields.");
      return;
    }
    const result = await submitLead({ source: "quote", ...form });
    if (result.ok) {
      toast.success("Quote request received");
    } else {
      toast.success("Got your request — we’ll be in touch shortly.");
    }
    trackEvent("lead_submit", {
      source: "quote_page",
      service: form.service,
      suburb: form.suburb,
      frequency: form.frequency,
    });
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Request a Quote | Premium House Cleaning Brisbane | The Sterling Touch"
        description="Request a personalised cleaning quote from The Sterling Touch. Tell us about your Brisbane home and we'll respond within 24 hours with a tailored package."
        path="/quote"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Request a Quote", path: "/quote" },
        ]}
      />
      <header className="sticky top-0 z-40 bg-cream/95 backdrop-blur border-b border-[#e8d9cc]">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-ink hover:text-[#b8866e] transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm">Back to home</span>
          </Link>
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_sterling-landing/artifacts/kxq2bn2y_The%20Sterling%20Touch%20Logo%20%289%29.png"
              alt="The Sterling Touch"
              className="w-14 h-14 object-contain"
            />
            <div className="font-brand text-base text-ink">The Sterling Touch</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        {!submitted ? (
          <>
            <div className="text-center">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#daae9c]">
                <span className="w-6 h-px bg-sterling-dark" />
                Personalised Quote
                <span className="w-6 h-px bg-sterling-dark" />
              </div>
              <h1 className="font-serif-display text-5xl text-ink mt-4 leading-tight">
                Tell us about your home
              </h1>
              <p className="text-ink/70 mt-3">
                Every home is assessed individually. We respond within 24 hours.
              </p>
            </div>

            <form
              onSubmit={onSubmit}
              className="mt-10 bg-white border border-[#ead8c9] rounded-3xl p-8 lg:p-10 shadow-[0_20px_60px_-20px_rgba(138,93,72,0.15)]"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-ink/70">Full name</Label>
                  <Input value={form.name} onChange={onChange("name")} className="mt-1.5 h-11 bg-white border-[#e8d9cc] rounded-lg" placeholder="Jane Doe" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink/70">Email</Label>
                  <Input type="email" value={form.email} onChange={onChange("email")} className="mt-1.5 h-11 bg-white border-[#e8d9cc] rounded-lg" placeholder="you@email.com" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink/70">Phone</Label>
                  <Input value={form.phone} onChange={onChange("phone")} className="mt-1.5 h-11 bg-white border-[#e8d9cc] rounded-lg" placeholder="04xx xxx xxx" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink/70">Suburb</Label>
                  <Input value={form.suburb} onChange={onChange("suburb")} className="mt-1.5 h-11 bg-white border-[#e8d9cc] rounded-lg" placeholder="e.g. New Farm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-ink/70">Service</Label>
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger className="mt-1.5 h-11 bg-white border-[#e8d9cc] rounded-lg">
                      <SelectValue placeholder="Choose" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.short}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-ink/70">Bedrooms</Label>
                    <Input value={form.bedrooms} onChange={onChange("bedrooms")} className="mt-1.5 h-11 bg-white border-[#e8d9cc] rounded-lg" placeholder="3" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-ink/70">Bathrooms</Label>
                    <Input value={form.bathrooms} onChange={onChange("bathrooms")} className="mt-1.5 h-11 bg-white border-[#e8d9cc] rounded-lg" placeholder="2" />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-ink/70">Frequency</Label>
                  <RadioGroup
                    value={form.frequency}
                    onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}
                    className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2"
                  >
                    {[
                      ["one-off", "One-off"],
                      ["weekly", "Weekly"],
                      ["fortnightly", "Fortnightly"],
                      ["monthly", "Monthly"],
                    ].map(([v, l]) => (
                      <label
                        key={v}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                          form.frequency === v
                            ? "border-sterling-dark bg-[#faf3eb]"
                            : "border-[#e8d9cc] bg-white"
                        }`}
                      >
                        <RadioGroupItem value={v} />
                        <span className="text-sm text-ink">{l}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-ink/70">Anything else?</Label>
                  <Textarea value={form.notes} onChange={onChange("notes")} rows={4} className="mt-1.5 bg-white border-[#e8d9cc] rounded-lg" placeholder="Share access notes, preferences, or specific areas." />
                </div>
              </div>

              <Button type="submit" className="mt-8 w-full h-12 bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors rounded-full">
                Request my quote <ArrowRight size={16} className="ml-2" />
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="mx-auto w-16 h-16 rounded-full bg-sterling/30 flex items-center justify-center">
              <CheckCircle2 size={36} className="text-[#daae9c]" />
            </div>
            <h1 className="font-serif-display text-5xl text-ink mt-6">Thank you.</h1>
            <p className="text-ink/70 mt-3 max-w-md mx-auto">
              Your request is in. We will be in touch within 24 hours with your personalised quote.
            </p>
            <Link to="/">
              <Button variant="outline" className="mt-8 rounded-full border-ink/20">
                Back to home
              </Button>
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default QuoteForm;
